/** @file encodePng: encodes an RGBA8 image as a PNG (colour type 6, depth 8, one IDAT). */
import { concatBytes } from '../bytes/concatBytes';
import type { Image } from '../types/image';
import type { ZlibCodec } from '../types/zlibCodec';
import { storedDeflate } from '../zlib/storedDeflate';
import { filterScanlines, type PngFilterMode } from './filterScanlines';
import { writeChunk } from './writeChunk';

/** `codec` absent: stored (uncompressed) deflate, no zlib needed at all; `filter` defaults to `'adaptive'`. */
export type PngEncodeOptions = { codec?: ZlibCodec; filter?: PngFilterMode };

const SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

/** PNG bytes of `image`: signature, IHDR, one IDAT, IEND; lossless for every RGBA8 image. */
export async function encodePng(image: Image, options: PngEncodeOptions = {}): Promise<Uint8Array> {
  const ihdr = new Uint8Array(13);
  const dv = new DataView(ihdr.buffer);
  dv.setUint32(0, image.width);
  dv.setUint32(4, image.height);
  ihdr.set([8, 6, 0, 0, 0], 8);
  const filtered = filterScanlines(image, options.filter ?? 'adaptive');
  const idat = options.codec === undefined ? storedDeflate(filtered) : await options.codec.deflate(filtered);
  return concatBytes([Uint8Array.from(SIGNATURE), writeChunk('IHDR', ihdr), writeChunk('IDAT', idat), writeChunk('IEND', new Uint8Array(0))]);
}
